/**
 * @file
 * RAOC-23: pure overlay that forces `userNeedsOAuth: false` onto the
 * `thirdPartyConfigs` by useRovoConfig. Designed to be supported by a
 * singleton store to track which products/datasources have been OAuth-connected.
 *
 * Extracted as a pure function (no React, no store) so it can be unit-tested in
 * isolation and shared by whichever shared-store implementation drives it. The
 * `isConnected` predicate is injected so the overlay does not couple to the
 * store mechanism (module singleton, react-sweet-state, etc.).
 */

import {
	type ThirdPartyConfigsBootstrap,
	type ThirdPartyConfigsWithAPIResponse,
} from '../../../common/types/schemas/types';

/**
 * Predicate: has this `product` (optionally a specific `datasourceId`) been
 * OAuth-connected?
 */
export type IsOAuthConnected = (product: string, datasourceId?: string) => boolean;

/**
 * Return a new `thirdPartyConfigs` where any product (or nested datasource)
 * flagged connected by `isConnected` has `userNeedsOAuth` forced to `false`.
 *
 * Additive and idempotent:
 * - never sets `userNeedsOAuth: true` (only ever clears it);
 * - leaves entries already at `false` untouched (and returns the *same* object
 *   reference when nothing changes, so referential-equality memoisation
 *   upstream is preserved and no needless re-render is triggered).
 *
 * @param configs the derived `thirdPartyConfigs` map (may be undefined).
 * @param isConnected predicate sourced from the shared OAuth-override store.
 */
export const applyOAuthOverrides = (
	configs: ThirdPartyConfigsBootstrap | undefined,
	isConnected: IsOAuthConnected,
): ThirdPartyConfigsBootstrap | undefined => {
	if (!configs) {
		return configs;
	}

	let next: ThirdPartyConfigsBootstrap | undefined;

	for (const [productKey, config] of Object.entries(configs)) {
		if (!config) {
			continue;
		}

		const productConnected = isConnected(productKey);

		// Compute any datasource-level overrides first.
		let nextDatasources: ThirdPartyConfigsWithAPIResponse[] | undefined;
		if (config.datasources?.length) {
			let datasourcesChanged = false;
			const mapped = config.datasources.map((ds) => {
				if (
					ds.userNeedsOAuth !== false &&
					ds.datasourceId != null &&
					isConnected(productKey, ds.datasourceId)
				) {
					datasourcesChanged = true;
					return { ...ds, userNeedsOAuth: false };
				}
				return ds;
			});
			if (datasourcesChanged) {
				nextDatasources = mapped;
			}
		}

		const needsProductFlip = productConnected && config.userNeedsOAuth !== false;

		if (!needsProductFlip && !nextDatasources) {
			continue;
		}

		const nextConfig: ThirdPartyConfigsWithAPIResponse = {
			...config,
			...(needsProductFlip ? { userNeedsOAuth: false } : {}),
			...(nextDatasources ? { datasources: nextDatasources } : {}),
		};

		next = next ?? { ...configs };
		next[productKey] = nextConfig;
	}

	return next ?? configs;
};
